/* HttpProvider — DataProvider over the Go API (contracts/API.md). */
import { ApiError, type DataProvider } from "../provider";
import type * as T from "../types";

type Query = Record<string, string | number | boolean | null | undefined>;

export interface HttpProviderOptions {
  /** API origin (without /api/v1). Default: VITE_API_URL or http://localhost:8080. */
  baseUrl?: string;
  token?: string | null;
  /** Injectable fetch (tests). Default: globalThis.fetch. */
  fetch?: typeof fetch;
}

interface RequestOpts {
  query?: Query;
  json?: unknown;
  text?: { body: string; contentType: string };
  form?: FormData;
  /** Expect a text response instead of JSON. */
  expectText?: boolean;
}

/** "?a=1&b=x" skipping undefined/null/"" values; "" when nothing to add. */
export function buildQuery(q?: Query): string {
  if (!q) return "";
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(q)) {
    if (v === undefined || v === null || v === "") continue;
    p.append(k, String(v));
  }
  const s = p.toString();
  return s ? "?" + s : "";
}

const enc = encodeURIComponent;

function defaultBaseUrl(): string {
  let env: string | undefined;
  try { env = import.meta.env?.VITE_API_URL as string | undefined; } catch { env = undefined; }
  return env ?? "http://localhost:8080";
}

export class HttpProvider implements DataProvider {
  readonly mode = "http" as const;
  readonly baseUrl: string;
  private token: string | null;
  private readonly fetchImpl: typeof fetch;

  constructor(opts: HttpProviderOptions | string = {}) {
    const o = typeof opts === "string" ? { baseUrl: opts } : opts;
    this.baseUrl = (o.baseUrl ?? defaultBaseUrl()).replace(/\/+$/, "");
    this.token = o.token ?? null;
    const f = o.fetch ?? (globalThis.fetch as typeof fetch | undefined);
    this.fetchImpl = f ? ((...a: Parameters<typeof fetch>) => f(...a)) as typeof fetch : (() => Promise.reject(new TypeError("fetch unavailable"))) as typeof fetch;
  }

  get apiBase(): string { return this.baseUrl + "/api/v1"; }
  setToken(token: string | null): void { this.token = token; }
  getToken(): string | null { return this.token; }

  /* ---------- transport ---------- */
  async request<R>(method: string, path: string, o: RequestOpts = {}): Promise<R> {
    const headers: Record<string, string> = { Accept: o.expectText ? "text/csv, text/plain, */*" : "application/json" };
    if (this.token) headers.Authorization = "Bearer " + this.token;
    let body: BodyInit | undefined;
    if (o.json !== undefined) { headers["Content-Type"] = "application/json"; body = JSON.stringify(o.json); }
    else if (o.text) { headers["Content-Type"] = o.text.contentType; body = o.text.body; }
    else if (o.form) body = o.form; // browser sets multipart boundary
    let res: Response;
    try {
      res = await this.fetchImpl(this.apiBase + path + buildQuery(o.query), { method, headers, body });
    } catch (e) {
      throw new ApiError(0, "network", "Network error: " + (e instanceof Error ? e.message : String(e)));
    }
    if (!res.ok) throw await toApiError(res);
    if (res.status === 204) return undefined as R;
    if (o.expectText) return (await res.text()) as R;
    const txt = await res.text();
    if (!txt) return undefined as R;
    try { return JSON.parse(txt) as R; } catch { throw new ApiError(res.status, "bad_response", "Invalid JSON from server"); }
  }

  private get<R>(path: string, query?: Query) { return this.request<R>("GET", path, { query }); }
  private post<R>(path: string, json: unknown = {}) { return this.request<R>("POST", path, { json }); }
  private put<R>(path: string, json: unknown) { return this.request<R>("PUT", path, { json }); }
  private patch<R>(path: string, json: unknown = {}) { return this.request<R>("PATCH", path, { json }); }
  private del(path: string) { return this.request<void>("DELETE", path); }

  /* ---------- auth ---------- */
  async login(req: T.LoginRequest): Promise<T.LoginResponse> {
    const r = await this.post<T.LoginResponse>("/auth/login", req);
    this.token = r.token;
    return r;
  }
  async switchRole(role: T.Role): Promise<T.LoginResponse> {
    const r = await this.post<T.LoginResponse>("/auth/switch-role", { role });
    this.token = r.token;
    return r;
  }
  me() { return this.get<T.User>("/me"); }
  clock() { return this.get<T.Clock>("/clock"); }
  async logout(): Promise<void> {
    try { await this.request<void>("POST", "/auth/logout"); } finally { this.token = null; }
  }
  updateMe(patch: { lang: T.Lang }) { return this.patch<T.User>("/me", patch); }

  /* ---------- self-signup, verification, passwords ---------- */
  signup(req: T.SignupRequest) { return this.post<T.SignupResponse>("/auth/signup", req); }
  async verifyEmail(token: string): Promise<T.LoginResponse> {
    const r = await this.post<T.LoginResponse>("/auth/verify-email", { token });
    this.token = r.token;
    return r;
  }
  async resendVerification(email: string): Promise<void> { await this.post<unknown>("/auth/resend-verification", { email }); }
  async forgotPassword(email: string): Promise<void> { await this.post<unknown>("/auth/forgot-password", { email }); }
  async resetPassword(token: string, password: string): Promise<{ email: string }> {
    const r = await this.post<{ ok: boolean; email: string }>("/auth/reset-password", { token, password });
    return { email: r.email };
  }
  async changePassword(current: string, next: string): Promise<T.LoginResponse> {
    const r = await this.post<T.LoginResponse>("/auth/change-password", { current, new: next });
    this.token = r.token;
    return r;
  }

  /* ---------- settings ---------- */
  getSettings() { return this.get<T.Settings>("/settings"); }
  updateSettings(patch: Partial<Omit<T.Settings, "tenant">> & { tenant?: Partial<T.Tenant> }) { return this.put<T.Settings>("/settings", patch); }
  getNotificationPrefs() { return this.get<T.NotificationPref[]>("/settings/notifications"); }
  updateNotificationPrefs(patch: Record<string, boolean>) { return this.put<T.NotificationPref[]>("/settings/notifications", patch); }

  /* ---------- employees ---------- */
  listEmployees(q: T.EmployeeQuery = {}) { return this.get<T.Page<T.Employee>>("/employees", { ...q }); }
  async allEmployees(): Promise<T.Employee[]> { return (await this.listEmployees({ pageSize: 1000 })).items; }
  getEmployee(id: number) { return this.get<T.Employee>(`/employees/${id}`); }
  createEmployee(input: T.EmployeeInput) { return this.post<T.Employee>("/employees", input); }
  updateEmployee(id: number, patch: Partial<T.EmployeeInput>) { return this.put<T.Employee>(`/employees/${id}`, patch); }
  importEmployees(rows: Partial<T.EmployeeInput>[]) { return this.post<T.ImportResult>("/employees/import", { rows }); }
  expiringDocuments(days: number) { return this.get<T.ExpiringDoc[]>("/documents/expiring", { days }); }
  renewDocument(employeeId: number, kind: string, expiry: string, fileKey?: string) {
    return this.post<T.Employee>(`/employees/${employeeId}/documents/${enc(kind)}/renew`, fileKey ? { expiry, fileKey } : { expiry });
  }

  inviteEmployee(id: number) { return this.post<T.InviteResult>(`/employees/${id}/invite`); }

  /* ---------- files ---------- */
  uploadFile(file: File | Blob, name: string, folder: string) {
    const fd = new FormData();
    fd.append("folder", folder);
    fd.append("name", name);
    fd.append("file", file, name);
    return this.request<T.FileRef>("POST", "/files", { form: fd });
  }
  fileUrl(key: string): string {
    const path = key.split("/").map(enc).join("/");
    return this.apiBase + "/files/" + path + (this.token ? "?token=" + enc(this.token) : "");
  }

  /* ---------- payroll ---------- */
  payrollHistory() { return this.get<T.PayrollHistoryItem[]>("/payroll/history"); }
  getPayrollRun() { return this.get<T.PayrollRunView>("/payroll/run"); }
  setAdjustment(employeeId: number, adj: T.Adjustment) { return this.put<T.PayrollRunView>(`/payroll/run/adjustments/${employeeId}`, adj); }
  importOvertimeFromAttendance() { return this.post<T.PayrollRunView>("/payroll/run/overtime-from-attendance"); }
  setRunStep(step: number) { return this.post<T.PayrollRunView>("/payroll/run/step", { step }); }
  approveRun() { return this.post<T.PayrollRunView>("/payroll/run/approve"); }
  exportWps() { return this.post<T.PayrollRunView>("/payroll/run/export"); }
  ackMudad() { return this.post<T.PayrollRunView>("/payroll/run/mudad-ack"); }
  markPaid() { return this.post<T.PayrollRunView>("/payroll/run/paid"); }
  getPayslip(employeeId: number, period: string) { return this.get<T.Payslip>(`/payroll/payslips/${employeeId}/${enc(period)}`); }

  /* ---------- leave ---------- */
  listLeaves() { return this.get<T.Leave[]>("/leaves"); }
  createLeave(input: T.LeaveInput) { return this.post<T.Leave>("/leaves", input); }
  decideLeave(id: number, approve: boolean) { return this.post<T.Leave>(`/leaves/${id}/decision`, { approve }); }
  leaveBalance(employeeId: number) { return this.get<T.LeaveBalance>(`/leaves/balance/${employeeId}`); }
  leavePolicies() { return this.get<T.LeavePolicy[]>("/leaves/policies"); }

  /* ---------- attendance ---------- */
  attendanceDay(date?: string) { return this.get<T.AttendanceDay>("/attendance/day", { date }); }
  attendanceMonth(employeeId: number, period: string) { return this.get<T.DailyAttendance[]>(`/attendance/month/${employeeId}/${enc(period)}`); }
  check(req: T.CheckRequest) { return this.post<T.Punch>("/attendance/check", req); }

  /* ---------- biometric ---------- */
  biometricOverview() { return this.get<T.BiometricOverview>("/biometric/overview"); }
  bioVendors() { return this.get<T.BioVendorInfo[]>("/biometric/vendors"); }
  connectionInfo() { return this.get<T.ConnectionInfo>("/biometric/connection-info"); }
  listDevices() { return this.get<T.Device[]>("/biometric/devices"); }
  createDevice(input: T.DeviceInput) { return this.post<T.Device>("/biometric/devices", input); }
  updateDevice(id: string, patch: Partial<T.DeviceInput>) { return this.put<T.Device>(`/biometric/devices/${enc(id)}`, patch); }
  deleteDevice(id: string) { return this.del(`/biometric/devices/${enc(id)}`); }
  claimDevice(id: string, input: { name: string; branch: string }) { return this.post<T.Device>(`/biometric/devices/${enc(id)}/claim`, input); }
  testDevice(id: string) { return this.post<{ ok: boolean; message: string }>(`/biometric/devices/${enc(id)}/test`); }
  syncDevice(id: string) { return this.post<T.IngestResult>(`/biometric/devices/${enc(id)}/sync`); }
  rotateDeviceToken(id: string) { return this.post<T.Device>(`/biometric/devices/${enc(id)}/rotate-token`); }
  listPunches(q: T.PunchQuery = {}) { return this.get<T.Page<T.Punch>>("/biometric/punches", { ...q }); }
  importPunches(deviceId: string, csv: string) {
    return this.request<T.IngestResult>("POST", `/biometric/devices/${enc(deviceId)}/import`, { text: { body: csv, contentType: "text/csv" } });
  }
  unmatchedUsers() { return this.get<T.UnmatchedUser[]>("/biometric/unmatched"); }
  listMappings() { return this.get<T.DeviceUserMap[]>("/biometric/mappings"); }
  mapUser(m: T.DeviceUserMap) { return this.post<T.DeviceUserMap>("/biometric/mappings", m); }
  unmapUser(deviceId: string, deviceUserId: string) { return this.del(`/biometric/mappings/${enc(deviceId)}/${enc(deviceUserId)}`); }
  listCommands(deviceId?: string) { return this.get<T.DeviceCommand[]>("/biometric/commands", { deviceId }); }
  queueCommand(deviceId: string, kind: T.DeviceCommand["kind"], payload?: Record<string, string>) {
    return this.post<T.DeviceCommand>(`/biometric/devices/${enc(deviceId)}/commands`, { kind, payload: payload ?? {} });
  }
  syncEmployeesToDevice(deviceId: string, employeeIds?: number[]) {
    return this.post<T.DeviceCommand[]>(`/biometric/devices/${enc(deviceId)}/push-users`, employeeIds ? { employeeIds } : {});
  }

  /* ---------- loans / letters / expenses / assets ---------- */
  listLoans() { return this.get<T.Loan[]>("/loans"); }
  createLoan(input: T.LoanInput) { return this.post<T.Loan>("/loans", input); }
  decideLoan(id: number, approve: boolean) { return this.post<T.Loan>(`/loans/${id}/decision`, { approve }); }
  listLetters() { return this.get<T.Letter[]>("/letters"); }
  createLetter(input: T.LetterInput) { return this.post<T.Letter>("/letters", input); }
  issueLetter(id: number) { return this.post<T.Letter>(`/letters/${id}/issue`); }
  listExpenses() { return this.get<T.Expense[]>("/expenses"); }
  createExpense(input: T.ExpenseInput) { return this.post<T.Expense>("/expenses", input); }
  decideExpense(id: number, approve: boolean) { return this.post<T.Expense>(`/expenses/${id}/decision`, { approve }); }
  listAssets() { return this.get<T.Asset[]>("/assets"); }
  createAsset(input: T.AssetInput) { return this.post<T.Asset>("/assets", input); }
  listSites() { return this.get<T.WorkSite[]>("/sites"); }
  createSite(input: T.WorkSiteInput) { return this.post<T.WorkSite>("/sites", input); }
  updateSite(id: string, patch: T.WorkSiteInput) { return this.put<T.WorkSite>("/sites/" + encodeURIComponent(id), patch); }

  /* ---------- lifecycle ---------- */
  listOnboarding() { return this.get<T.Onboarding[]>("/onboarding"); }
  toggleOnboardingTask(employeeId: number, index: number) { return this.patch<T.Onboarding>(`/onboarding/${employeeId}/tasks/${index}`); }
  listOffboarding() { return this.get<T.Offboarding[]>("/offboarding"); }
  createOffboarding(input: T.OffboardingInput) { return this.post<T.Offboarding>("/offboarding", input); }
  toggleClearance(id: number, index: number) { return this.patch<T.Offboarding>(`/offboarding/${id}/clearance/${index}`); }
  acknowledgeResignation(id: number) { return this.post<T.Offboarding>(`/offboarding/${id}/acknowledge`); }
  getSettlement(id: number) { return this.get<T.Settlement>(`/offboarding/${id}/settlement`); }
  eosbQuote(employeeId: number, end: string, reason: string) { return this.get<T.EosbResult>("/eosb", { employeeId, end, reason }); }

  /* ---------- government ---------- */
  listGovTransactions() { return this.get<T.GovTransaction[]>("/gov/transactions"); }
  createGovTransaction(input: T.GovTransactionInput) { return this.post<T.GovTransaction>("/gov/transactions", input); }
  govFee(type: string, months: number) { return this.get<T.FeeResult>("/gov/fees", { type, months }); }

  /* ---------- saudization ---------- */
  saudization(saudiHires?: number, nonSaudiHires?: number) { return this.get<T.NitaqatResult>("/saudization", { saudiHires, nonSaudiHires }); }
  authenticatePending() { return this.post<T.NitaqatResult>("/saudization/authenticate-pending"); }

  /* ---------- lists ---------- */
  listCases() { return this.get<T.Case[]>("/cases"); }
  listCandidates() { return this.get<T.Candidate[]>("/candidates"); }
  moveCandidate(id: number, stage: string) { return this.patch<T.Candidate>(`/candidates/${id}`, { stage }); }
  listGoals() { return this.get<T.Goal[]>("/goals"); }
  listAudit() { return this.get<T.AuditEntry[]>("/audit"); }
  listApprovals() { return this.get<T.Approval[]>("/approvals"); }
  listNotifications() { return this.get<T.Notification[]>("/notifications"); }
  markNotificationsRead() { return this.request<void>("POST", "/notifications/read"); }

  /* ---------- billing ---------- */
  getBilling() { return this.get<T.BillingView>("/billing"); }
  submitPayment(input: T.PaymentInput) { return this.post<T.Payment>("/billing/payments", input); }

  /* ---------- admin ---------- */
  adminOverview() { return this.get<T.AdminOverview>("/admin/overview"); }
  adminPayments(status?: string) { return this.get<T.Payment[]>("/admin/payments", { status }); }
  verifyPayment(id: string) { return this.post<T.Payment>(`/admin/payments/${enc(id)}/verify`); }
  rejectPayment(id: string, reason: string) { return this.post<T.Payment>(`/admin/payments/${enc(id)}/reject`, { reason }); }
  adminTenants() { return this.get<T.TenantSummary[]>("/admin/tenants"); }
  extendTrial(tenantId: string, days: number) { return this.post<T.TenantSummary>(`/admin/tenants/${enc(tenantId)}/extend-trial`, { days }); }
  adminPlans() { return this.get<Record<string, number>>("/admin/plans"); }
  adminBankAccounts() { return this.get<T.BankAccount[]>("/admin/bank-accounts"); }
  updatePlanPrices(prices: Record<string, number>) { return this.put<Record<string, number>>("/admin/plans", prices); }
  updateBankAccount(index: number, acct: Partial<T.BankAccount>) { return this.put<T.BankAccount[]>(`/admin/bank-accounts/${index}`, acct); }
  getAdminAi() { return this.get<T.AiConfig>("/admin/ai"); }
  updateAdminAi(patch: Partial<Pick<T.AiConfig, "residency" | "budget" | "credits">>) { return this.put<T.AiConfig>("/admin/ai", patch); }
  connectAiProvider(id: string, apiKey: string, model?: string) {
    return this.post<T.AiConfig>(`/admin/ai/providers/${enc(id)}/connect`, model ? { apiKey, model } : { apiKey });
  }
  disconnectAiProvider(id: string) { return this.post<T.AiConfig>(`/admin/ai/providers/${enc(id)}/disconnect`); }
  adminStorage() { return this.get<T.StorageConfig>("/admin/storage"); }
  updateAdminStorage(input: T.StorageInput) { return this.put<T.StorageConfig>("/admin/storage", input); }
  resetAdminStorage() { return this.request<T.StorageConfig>("DELETE", "/admin/storage"); }
  testAdminStorage(input?: T.StorageInput) { return this.post<T.StorageTestResult>("/admin/storage/test", input ?? {}); }
  adminEmail() { return this.get<T.EmailConfig>("/admin/email"); }
  updateAdminEmail(input: T.EmailConfigInput) { return this.put<T.EmailConfig>("/admin/email", input); }
  resetAdminEmail() { return this.request<T.EmailConfig>("DELETE", "/admin/email"); }
  testAdminEmail(req: T.EmailTestRequest) { return this.post<T.EmailTestResult>("/admin/email/test", req); }
  adminEmailLogs(q: T.EmailLogQuery = {}) { return this.get<T.Page<T.EmailLog>>("/admin/email/logs", { ...q }); }
  adminEmailLog(id: string) { return this.get<T.EmailLogDetail>(`/admin/email/logs/${enc(id)}`); }
  resendEmail(id: string) { return this.post<T.EmailLog>(`/admin/email/logs/${enc(id)}/resend`); }
  adminEmailTemplates() { return this.get<T.EmailTemplateInfo[]>("/admin/email/templates"); }
  previewEmailTemplate(key: string, lang: T.Lang | "bi") { return this.get<T.EmailPreview>(`/admin/email/templates/${enc(key)}/preview`, { lang }); }
  adminEmailDns(q: { provider?: string; region?: string; from?: string } = {}) { return this.get<T.DnsGuidance>("/admin/email/dns", { ...q }); }
  adminSuppressions() { return this.get<T.Suppression[]>("/admin/email/suppressions"); }
  addSuppression(email: string, reason?: T.Suppression["reason"], note?: string) {
    return this.post<T.Suppression>("/admin/email/suppressions", { email, ...(reason ? { reason } : {}), ...(note ? { note } : {}) });
  }
  removeSuppression(email: string) { return this.del(`/admin/email/suppressions/${enc(email)}`); }

  /* ---------- AI ---------- */
  aiFeatures() { return this.get<T.AiConfig>("/ai/features"); }
  updateAiFeature(id: string, patch: Partial<Pick<T.AiFeature, "enabled" | "model">>) { return this.put<T.AiFeature>(`/ai/features/${enc(id)}`, patch); }
  aiExtract(req: T.AiExtractRequest) { return this.post<T.AiExtractResponse>("/ai/extract", req); }
  aiChat(req: T.AiChatRequest) { return this.post<T.AiChatResponse>("/ai/chat", req); }

  /* ---------- reports ---------- */
  reportCsv(kind: "register" | "gosi" | "eosb" | "employees" | "attendance") {
    return this.request<string>("GET", `/reports/${kind}.csv`, { expectText: true });
  }
}

/** Map a non-2xx response to ApiError(status, code, message, fields). */
export async function toApiError(res: Response): Promise<ApiError> {
  let text = "";
  try { text = await res.text(); } catch { /* ignore */ }
  try {
    const b = JSON.parse(text) as Partial<T.ApiErrorBody>;
    if (b && b.error && typeof b.error.code === "string") return new ApiError(res.status, b.error.code, b.error.message || res.statusText, b.error.fields);
  } catch { /* not JSON */ }
  const code = res.status === 400 ? "bad_request" : res.status === 401 ? "unauthorized" : res.status === 403 ? "forbidden" : res.status === 404 ? "not_found"
    : res.status === 409 ? "conflict" : res.status === 413 ? "bad_request" : res.status === 422 ? "validation" : res.status >= 500 ? "internal" : "http_" + res.status;
  return new ApiError(res.status, code, text.trim().slice(0, 200) || res.statusText || "HTTP " + res.status);
}
